import { Injectable } from '@angular/core';
import { BaseRepositoryService } from './base.service';
import { Module } from '../model/module.model';
import { AuthService } from './auth.service';
import { EntityService } from './entity.service';
import { Entity } from '../model/entity.model';
import { Page } from '../model/page.model';
import { RoleService } from './role.service';
import { PageService } from './page.service';
import { OotbEntityService } from './ootb-entity.service';
import { FunctionListService } from './function-list.service';
import { FunctionListGlobalService } from './function-list-global.service';
import { FunctionInterface } from '../model/function.model';
import { firstValueFrom } from 'rxjs';
import { doc, collection, setDoc, Firestore, getDocs, deleteDoc } from '@angular/fire/firestore';
import { inject } from '@angular/core';


import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';
@Injectable({
  providedIn: 'root'
})
export class ModuleService extends BaseRepositoryService<Module> {
  override collectionPath: string = '/modules';
  private _ready!: Promise<void>;
  private firestore = inject(Firestore);

  constructor(
    private authService: AuthService,
    private entityService: EntityService,
    private roleService: RoleService,
    private pageService: PageService,
    private ootbEntityService: OotbEntityService,
    private functionListService: FunctionListService,
    private functionListGlobalService: FunctionListGlobalService
  ) { 
    super();
    // this.collectionPath = 'modules';
    this._ready = this.getData();
  }

  ready() {
    return this._ready;
  }

  async getData() {
    await this.query(1000);
  }

  /**
   * Install a module - creates all associated entities, pages, and roles
   */
  async installModule(
    module: Module, 
    options: {
      importOotb: boolean;
      selectedOotbEntityIds?: string[];
      selectedEntityIds: string[];
      selectedPageIds: string[];
      selectedRoleIds: string[];
    }
  ): Promise<void> {
    try {
      // Import OOTB entities
      if (options.importOotb) {
        // Import all OOTB entities
        const ootbEntities = await this.getModuleOotbEntities(module.id);
        for (const entity of ootbEntities) {
          entity.moduleSource = module.name;
          await this.ootbEntityService.create(entity);
        }
      } else if (options.selectedOotbEntityIds && options.selectedOotbEntityIds.length > 0) {
        // Import selected OOTB entities
        const allOotbEntities = await this.getModuleOotbEntities(module.id);
        const selectedOotbEntities = allOotbEntities.filter(e => 
          options.selectedOotbEntityIds!.includes(e.id)
        );
        
        for (const entity of selectedOotbEntities) {
          entity.moduleSource = module.name;
          await this.ootbEntityService.create(entity);
        }
      }
      // Import all roles (always imported)
      const allRoles = await this.getModuleRoles(module.id);
      for (const role of allRoles) {
        role.moduleSource = module.name;
        // Was (2026-10-06, ws 9a W9): await this.roleService.create(role);
        // create() now throws on a refusal; a module install never failed on a role before, so keep it that way.
        try { await this.roleService.create(role); } catch (e) { console.warn('Module role not created:', role.id, e); }
      }
      
      console.log(`Module ${module.name} installed successfully`);
    } catch (error) {
      console.error('Error installing module:', error);
      throw error;
    }
  }

  /**
   * Get entities from a module's subcollection
   */
  async getModuleEntities(moduleId: string): Promise<Entity[]> {
    const entitiesRef = collection(this.firestore, 'modules', moduleId, 'entities');
    const snapshot = await getDocs(entitiesRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Entity));
  }

  /**
   * Get OOTB entities from a module's subcollection
   */
  async getModuleOotbEntities(moduleId: string): Promise<Entity[]> {
    const entitiesRef = collection(this.firestore, 'modules', moduleId, 'ootbEntities');
    const snapshot = await getDocs(entitiesRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Entity));
  }

  /**
   * Get pages from a module's subcollection
   */
  async getModulePages(moduleId: string): Promise<Page[]> {
    const pagesRef = collection(this.firestore, 'modules', moduleId, 'pages');
    const snapshot = await getDocs(pagesRef);
    // do this before resturning the pages
    const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
     for(let i=0 ; i< results.length ; i++) {
            if(results[i]['blobData']) {
              const decompressed = decompressFromEncodedURIComponent(results[i]['blobData']);
             if (decompressed) {
              results[i] = JSON.parse(decompressed);

              } else {
              results[i] = JSON.parse(results[i]['blobData']);
              }
            }
          }
    return results as Page[];
  }

  /**
   * Get functions from a module's subcollection
   */
  async getModuleFunctions(moduleId: string): Promise<FunctionInterface[]> {
    const functionsRef = collection(this.firestore, 'modules', moduleId, 'functions');
    const snapshot = await getDocs(functionsRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FunctionInterface));
  }

  /**
   * Get roles from a module's subcollection
   */
  async getModuleRoles(moduleId: string): Promise<any[]> {
    const rolesRef = collection(this.firestore, 'modules', moduleId, 'roles');
    const snapshot = await getDocs(rolesRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }

  /**
   * Check and return all functions that will be moved to the module
   */
  async getFunctionsToMove(): Promise<FunctionInterface[]> {
    try {
      const allLocalFunctions = await firstValueFrom(this.functionListService.$values);
      return allLocalFunctions || [];
    } catch (error) {
      console.error('Error getting functions to move:', error);
      return [];
    }
  }

  /**
   * Convert current client to a module
   * Saves entities, pages, roles, and functions as subcollections
   * Moves ALL local functions to the module without conditions
   */
  async convertClientToModule(
    moduleName: string, 
    description: string, 
    imageUrl?: string,
    functionsToArchive: FunctionInterface[] = []
  ): Promise<Module> {
    try {
      // Get client ID from auth service
      const clientId = this.authService.baseCollectionPath.split('/')[1];
      
      // Get all entities
      const allEntities = await firstValueFrom(this.entityService.$values);
      
      // Get OOTB entities
      const ootbEntities = await firstValueFrom(this.ootbEntityService.$values);
      const ootbEntityIds = new Set(ootbEntities.map(e => e.id));
      
      // Separate normal entities (excluding OOTB)
      const normalEntities = allEntities.filter(e => !ootbEntityIds.has(e.id));
      
      // Get all pages
      const allPages = await firstValueFrom(this.pageService.$values);
      
      // Get all roles
      const allRoles = await firstValueFrom(this.roleService.$values);

      
      // Create module ID
      const moduleId = `${moduleName.toLowerCase().replace(/[^a-z0-9_]/g, '_')}_module_${Date.now()}`;
      
      // Create module document
      const module: Module = {
        id: moduleId,
        name: moduleName,
        description: description,
        version: '1.0.0',
        category: 'CUSTOM',
        isActive: true,
        clientId: clientId,
        imageUrl: imageUrl,
        entityCount: normalEntities.length,
        ootbEntityCount: ootbEntities.length,
        pageCount: allPages.length,
        roleCount: allRoles.length,
        functionCount: functionsToArchive.length
      };
      
      // Save the main module document
      console.log('Creating module document:', module);
      // await this.create(module);
      
      // Save normal entities as subcollection
      for (const entity of normalEntities) {
        const entityDoc = doc(
          collection(this.firestore, 'modules', moduleId, 'entities'),
          entity.id
        );
        // await setDoc(entityDoc, entity);
        console.log('Saving entity to module subcollection:', entity);
      }
      
      // Move OOTB entities to module and global archive
      for (const entity of ootbEntities) {
        // 1. Save to module's OOTB subcollection
        const moduleOotbDoc = doc(
          collection(this.firestore, 'modules', moduleId, 'ootbEntities'),
          entity.id
        );
        // await setDoc(moduleOotbDoc, entity);
        console.log('Saving OOTB entity to module subcollection:', entity);
        
        // 2. Move to global module OOTB archive (for module distribution)
        const globalArchiveDoc = doc(
          collection(this.firestore, 'module_ootb_entities_archive'),
          entity.id
        );
        // await setDoc(globalArchiveDoc, entity);
        console.log('Saving OOTB entity to global archive:', entity);
        
        // 3. Delete from active OOTB collection (users can only access via modules now)
        const activeOotbDoc = doc(
          collection(this.firestore, 'entity'),
          entity.id
        );
        // await deleteDoc(activeOotbDoc);
        console.log('Deleting OOTB entity from active collection:', entity);
      }
      
      // Save pages as subcollection
      for (const page of allPages) {
        const pageDoc = doc(
          collection(this.firestore, 'modules', moduleId, 'pages'),
          page.id
        );
        // await setDoc(pageDoc, { blobData: compressToEncodedURIComponent(JSON.stringify(page))});
        console.log('Saving page to module subcollection:', page);
      }
      
      // Save roles as subcollection
      for (const role of allRoles) {
        const roleDoc = doc(
          collection(this.firestore, 'modules', moduleId, 'roles'),
          role.id
        );
        // await setDoc(roleDoc, role);
        console.log('Saving role to module subcollection:', role);
      }

      // Save imported functions as subcollection
      for (const func of functionsToArchive || []) {
        const funcDoc = doc(
          collection(this.firestore, 'modules', moduleId, 'functions'),
          func.id
        );
        // await setDoc(funcDoc, func);
        console.log('Saving function to module subcollection:', func);
      }
      // push to archive also
      for (const func of functionsToArchive || []) {
        const archiveDoc = doc(this.firestore, 'module_functions_archive', func.id);
        // await setDoc(archiveDoc, func);
        console.log('Saving function to global archive:', func);
      }
      
      // Delete ALL local functions from global level
      for (const func of functionsToArchive || []) {
        const globalFuncDoc = doc(this.firestore, 'function', func.id);
        // await deleteDoc(globalFuncDoc);
        console.log('Deleting function from global collection:', func);
      }
      return module;
    } catch (error) {
      console.error('Error converting client to module:', error);
      throw error;
    }
  }

}
